import React from "react";
import PropTypes from "prop-types";
import Grid from "@mui/material/Grid";
import {
  OTHER_OPTION_VALUE,
  MAX_EMPLOYMENT_TYPE_OTHER_LENGTH,
  MAX_DESIGNATION_LENGTH
} from "./constants";
import { MAX_COMPANY_NAME_LENGTH } from "../../../CompanyInfo/constants";
import {
  Input,
  Checkbox,
  AutoSaveField as Field,
  SelectAutocomplete
} from "../../../../components/Form";
import { MAX_EXPERIENCE_YEARS_LENGTH } from "../../../../utils/validation";
import { Card } from "../../../../components/v2/Card/Card";

export const SignatoryEmploymentDetailsComponent = ({
  index,
  companyName,
  isShareholderACompany,
  signatoryDocs,
  isFieldsRequired = true,
  values,
  setFieldValue
}) => {
  const basePath = `prospect.signatoryInfo[${index}].kycDetails`;

  return (
    <Card title="Employment details">
      <Grid container spacing={3} sx={{ mt: 1 }}>
        <Grid item sm={12} xs={12}>
          <Field
            name="uploadCV"
            path={`${basePath}.isUploadCV`}
            component={Checkbox}
            label={"I will upload CV"}
            inputProps={{ tabIndex: 0 }}
            contextualHelpProps={{ isDisableHoverListener: false }}
          />
        </Grid>
      </Grid>
      <Grid container spacing={3} sx={{ mt: 1 }}>
        <Grid item sm={6} xs={12}>
          <Field
            name="qualification"
            path={`${basePath}.qualification`}
            disabled={values.uploadCV}
            datalistId="qualification"
            label={
              values.uploadCV || isShareholderACompany || !isFieldsRequired
                ? "Qualification (optional)"
                : "Qualification"
            }
            isSearchable
            component={SelectAutocomplete}
            tabIndex="0"
          />
        </Grid>
        <Grid item sm={6} xs={12}>
          <Field
            name="employmentType"
            path={`${basePath}.employmentDetails.employmentType`}
            disabled={values.uploadCV}
            datalistId="employmentType"
            label={
              values.uploadCV || isShareholderACompany || !isFieldsRequired
                ? "Employment Type (optional)"
                : "Employment Type"
            }
            isSearchable
            component={SelectAutocomplete}
            contextualHelpProps={{ isDisableHoverListener: false }}
            contextualHelpText={
              <>
                If self-employed then provide business details
                <br />
                If unemployed, then select &apos;Other&apos;
              </>
            }
            tabIndex="0"
          />
        </Grid>
      </Grid>

      {values.employmentType === OTHER_OPTION_VALUE && (
        <Grid container spacing={3} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <Field
              name="otherEmploymentType"
              path={`${basePath}.employmentDetails.otherEmploymentType`}
              disabled={values.uploadCV}
              label="Other(Specify)"
              placeholder="Other(Specify)"
              component={Input}
              InputProps={{
                inputProps: { maxLength: MAX_EMPLOYMENT_TYPE_OTHER_LENGTH, tabIndex: 0 }
              }}
            />
          </Grid>
        </Grid>
      )}

      <Grid container spacing={3} sx={{ mt: 1 }}>
        <Grid item xs={12}>
          <Field
            name="designation"
            path={`${basePath}.employmentDetails.designation`}
            disabled={values.uploadCV}
            label={
              values.uploadCV || isShareholderACompany || !isFieldsRequired
                ? "Designation (optional)"
                : "Designation"
            }
            placeholder="Designation"
            component={SelectAutocomplete}
            datalistId="individualOccupation"
            contextualHelpText="If unemployed, then mention the designation as 'Unemployed'"
            InputProps={{
              inputProps: { maxLength: MAX_DESIGNATION_LENGTH, tabIndex: 0 }
            }}
          />
        </Grid>
      </Grid>
      <Grid container spacing={3} sx={{ mt: 1 }}>
        <Grid item xs={12}>
          <Field
            name={`isWorkAtTheCompany${index}`}
            path={`${basePath}.employmentDetails.isPersonWorkAtCompany`}
            label={`This person works at ${companyName}`}
            disabled={values.uploadCV}
            component={Checkbox}
            onSelect={() => {
              const employerName =
                values[`isWorkAtTheCompany${index}`] || values.employerName ? " " : companyName;
              setFieldValue("employerName", employerName);
            }}
            inputProps={{ tabIndex: 0 }}
          />
        </Grid>
      </Grid>
      <Grid container spacing={3} sx={{ mt: 1 }}>
        <Grid item xs={12}>
          <Field
            name="employerName"
            path={`${basePath}.employmentDetails.employerName`}
            label={
              values.uploadCV || isShareholderACompany || !isFieldsRequired
                ? "Employer name / Company name (optional)"
                : "Employer name / Company name"
            }
            placeholder="Employer name"
            component={Input}
            disabled={values[`isWorkAtTheCompany${index}`] || values.uploadCV}
            InputProps={{
              inputProps: { maxLength: MAX_COMPANY_NAME_LENGTH, tabIndex: 0 }
            }}
          />
        </Grid>
      </Grid>
      <Grid container spacing={3} sx={{ mt: 1 }}>
        <Grid item xs={12}>
          {`Share details about your professional experience${
            values.uploadCV || isShareholderACompany || !isFieldsRequired ? " (optional)" : ""
          }`}
        </Grid>
        <Grid item xs={12}>
          <Field
            name="totalExperienceYrs"
            path={`${basePath}.employmentDetails.signatoryBackgroundInfo`}
            placeholder="Enter here"
            component={Input}
            disabled={values.uploadCV}
            multiline
            rows="4"
            InputProps={{
              inputProps: { maxLength: MAX_EXPERIENCE_YEARS_LENGTH, tabIndex: 0 }
            }}
            contextualHelpText={
              <>
                <b>
                  {"<"}Please DON{"'"}T use ENTER key in this field{">"}
                </b>
                <br />
                <br />
                Starting with the most recent enter jobwise list of experience:
                <br />
                From Month-Year, To Month-Year, Company Name, Company Country, Position & Employment
                Type (Salaried / Self Employed)
                <br />
                <br />
                Example
                <br />
                APR-16 to TodaysDate, Reliance Biz, UAE, Proprietor, Self-Employed
                <br />
                AUG-13 to MAR-16, TCS, India, Marketing Manager, Salaried
              </>
            }
          />
        </Grid>
      </Grid>
    </Card>
  );
};

// PropTypes validation
SignatoryEmploymentDetailsComponent.propTypes = {
  /** Index of the signatory in the array */
  index: PropTypes.number.isRequired,
  /** Company name to be used in form labels */
  companyName: PropTypes.string.isRequired,
  /** Flag indicating if shareholder is a company */
  isShareholderACompany: PropTypes.bool,
  /** Documents related to the signatory */
  signatoryDocs: PropTypes.array,
  /** Flag indicating if fields are required */
  isFieldsRequired: PropTypes.bool,
  /** Form values object */
  values: PropTypes.shape({
    /** Flag indicating if CV will be uploaded */
    uploadCV: PropTypes.bool,
    /** Qualification value */
    qualification: PropTypes.string,
    /** Employment type value */
    employmentType: PropTypes.string,
    /** Other employment type value when employmentType is 'OTHER' */
    otherEmploymentType: PropTypes.string,
    /** Designation value */
    designation: PropTypes.string,
    /** Dynamic property indicating if person works at the company */
    isWorkAtTheCompany: PropTypes.bool,
    /** Employer name value */
    employerName: PropTypes.string,
    /** Total experience in years */
    totalExperienceYrs: PropTypes.string
  }).isRequired,
  /** Function to set form field values */
  setFieldValue: PropTypes.func.isRequired
};
